import { Utensils, Wine, GlassWater, Grape, Music } from "lucide-react";

const iconMap = {
  meal: Utensils,
  drink: Wine,
  mocktail: GlassWater,
};

const accentMap = {
  meal: "#E50914",
  drink: "#F5B841",
  mocktail: "#5EC5A0",
};

export const RecipeBlock = ({ type = "meal", label, name, ingredients = [], recipe = [] }) => {
  const Icon = iconMap[type];
  const accent = accentMap[type];
  return (
    <div
      className="rounded-2xl border border-white/5 bg-[#0d0d0d] p-6 sm:p-7"
      data-testid={`recipe-block-${type}`}
    >
      <div className="flex items-center gap-3 mb-1">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accent}22` }}
        >
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <span
          className="uppercase text-xs tracking-[0.2em] font-medium"
          style={{ color: accent }}
        >
          {label}
        </span>
      </div>
      <h3 className="font-display text-2xl font-bold mb-5 leading-tight">{name}</h3>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
            Ingredients
          </p>
          <ul className="space-y-1.5">
            {ingredients.map((ing, i) => (
              <li key={i} className="text-sm text-white/75 flex gap-2">
                <span style={{ color: accent }}>•</span>
                <span>{ing}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
            Recipe
          </p>
          <ol className="space-y-2.5">
            {recipe.map((step, i) => (
              <li key={i} className="text-sm text-white/75 flex gap-3">
                <span
                  className="font-display font-bold shrink-0"
                  style={{ color: accent }}
                >
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export const WineMusicBlock = ({ wine, music }) => {
  if (!wine && !music) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="wine-music-block">
      {wine && (
        <div className="rounded-2xl border border-white/5 bg-[#0d0d0d] p-6 flex gap-4">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#8b1e3f]/25">
            <Grape className="w-5 h-5 text-[#c96a86]" />
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#c96a86] mb-1.5">
              Wine Pairing
            </p>
            <p className="text-sm text-white/80 leading-relaxed">{wine}</p>
          </div>
        </div>
      )}
      {music && (
        <div className="rounded-2xl border border-white/5 bg-[#0d0d0d] p-6 flex gap-4">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#5EC5A0]/20">
            <Music className="w-5 h-5 text-[#5EC5A0]" />
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#5EC5A0] mb-1.5">
              Soundtrack Vibe
            </p>
            <p className="text-sm text-white/80 leading-relaxed">{music}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const PairingRecipes = ({ pairing }) => (
  <div className="space-y-5" data-testid="pairing-recipes">
    <RecipeBlock
      type="meal"
      label="The Meal"
      name={pairing.meal_name}
      ingredients={pairing.meal_ingredients}
      recipe={pairing.meal_recipe}
    />
    <RecipeBlock
      type="drink"
      label="The Cocktail"
      name={pairing.drink_name}
      ingredients={pairing.drink_ingredients}
      recipe={pairing.drink_recipe}
    />
    <RecipeBlock
      type="mocktail"
      label="Non-Alcoholic"
      name={pairing.non_alcoholic_drink_name}
      ingredients={pairing.non_alcoholic_drink_ingredients}
      recipe={pairing.non_alcoholic_drink_recipe}
    />
    <WineMusicBlock wine={pairing.wine_pairing} music={pairing.soundtrack_vibe} />
  </div>
);
